import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, Upload, LogOut, UtensilsCrossed, Inbox, ImageIcon, Pencil, KeyRound } from "lucide-react";
import { CATEGORY_OPTIONS } from "@/lib/data";
import {
  fetchMe, clearToken, getToken, fetchMenuItems, createMenuItem, deleteMenuItem,
  uploadImage, resolveImg, fetchBookings,
} from "@/lib/api";
import EditItemDialog from "@/components/admin/EditItemDialog";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";

const inputCls =
  "w-full bg-transparent border-b border-[#FDFBF7]/20 focus:border-[#D4AF37] outline-none py-2.5 font-body text-[#FDFBF7] placeholder:text-[#A89F91]/50 transition-colors duration-300";
const labelCls = "block font-body text-[11px] tracking-[0.2em] uppercase text-[#A89F91] mb-1";

const EMPTY = {
  category: CATEGORY_OPTIONS[0], name: "", description: "", image_url: "",
  half_tray_price: "", full_tray_price: "", serves_qty: "", serves_price: "",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("menu");
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [customCat, setCustomCat] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!getToken()) return navigate("/admin/login");
    fetchMe()
      .then(() => setChecking(false))
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const loadItems = () => fetchMenuItems().then(setItems).catch(() => {});
  const loadBookings = () => fetchBookings().then(setBookings).catch(() => {});

  useEffect(() => {
    if (!checking) {
      loadItems();
      loadBookings();
    }
  }, [checking]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const category = customCat.trim() || form.category;
    if (!form.name.trim()) return toast.error("Please enter a dish name.");
    if (!form.half_tray_price && !form.full_tray_price && !form.serves_price)
      return toast.error("Add at least one price (half tray, full tray or serves).");
    setSaving(true);
    try {
      await createMenuItem({ ...form, category });
      toast.success(`${form.name} added to the menu`);
      setForm(EMPTY);
      setCustomCat("");
      if (fileRef.current) fileRef.current.value = "";
      loadItems();
    } catch {
      toast.error("Could not save item.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await deleteMenuItem(id);
      toast.success("Item deleted");
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  if (checking) {
    return <div className="min-h-screen bg-pof flex items-center justify-center text-[#D4AF37]"><Loader2 className="animate-spin" size={32} /></div>;
  }

  const grouped = items.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-pof">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A0607]/85 border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Palace of Flavor" className="h-12 w-auto" />
            <span className="hidden sm:block font-display text-xl text-[#FDFBF7]">Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            <button data-testid="tab-menu" onClick={() => setTab("menu")} className={`inline-flex items-center gap-2 px-4 py-2 font-body text-xs tracking-[0.15em] uppercase transition-colors ${tab === "menu" ? "text-[#0A0607] bg-[#D4AF37]" : "text-[#A89F91] hover:text-[#D4AF37]"}`}>
              <UtensilsCrossed size={15} /> Menu
            </button>
            <button data-testid="tab-bookings" onClick={() => setTab("bookings")} className={`inline-flex items-center gap-2 px-4 py-2 font-body text-xs tracking-[0.15em] uppercase transition-colors ${tab === "bookings" ? "text-[#0A0607] bg-[#D4AF37]" : "text-[#A89F91] hover:text-[#D4AF37]"}`}>
              <Inbox size={15} /> Inquiries
            </button>
            <button data-testid="admin-change-password" onClick={() => setPwOpen(true)} className="ml-2 inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] font-body text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37]/10 transition-colors">
              <KeyRound size={15} /> <span className="hidden sm:inline">Password</span>
            </button>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] font-body text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37]/10 transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {tab === "menu" ? (
          <div className="grid lg:grid-cols-[380px_1fr] gap-10">
            {/* Add form */}
            <div className="lg:sticky lg:top-28 self-start">
              <h2 className="font-display text-3xl text-[#FDFBF7] mb-1">Add a Dish</h2>
              <p className="font-body text-sm text-[#A89F91] mb-6">Prices are per tray. Fill any that apply.</p>
              <form onSubmit={submit} data-testid="add-item-form" className="space-y-5 border border-[#D4AF37]/15 rounded-tl-[2rem] rounded-br-[2rem] p-6 bg-[#140A0D]">
                <div>
                  <label className={labelCls}>Category</label>
                  <select data-testid="item-category" className={`${inputCls} appearance-none cursor-pointer`} value={form.category} onChange={set("category")}>
                    {CATEGORY_OPTIONS.map((c) => <option key={c} value={c} className="bg-[#140A0D]">{c}</option>)}
                  </select>
                  <input data-testid="item-custom-category" className={`${inputCls} mt-2`} placeholder="or type a new category" value={customCat} onChange={(e) => setCustomCat(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Dish Name *</label>
                  <input data-testid="item-name" className={inputCls} placeholder="e.g. Butter Chicken" value={form.name} onChange={set("name")} />
                </div>
                <div>
                  <label className={labelCls}>Description</label>
                  <textarea data-testid="item-description" rows={2} className={`${inputCls} resize-none`} placeholder="Short description" value={form.description} onChange={set("description")} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Half Tray $</label>
                    <input data-testid="item-half-price" className={inputCls} placeholder="45" value={form.half_tray_price} onChange={set("half_tray_price")} />
                  </div>
                  <div>
                    <label className={labelCls}>Full Tray $</label>
                    <input data-testid="item-full-price" className={inputCls} placeholder="80" value={form.full_tray_price} onChange={set("full_tray_price")} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Serves Qty</label>
                    <input data-testid="item-serves-qty" className={inputCls} placeholder="10" value={form.serves_qty} onChange={set("serves_qty")} />
                  </div>
                  <div>
                    <label className={labelCls}>Serves $</label>
                    <input data-testid="item-serves-price" className={inputCls} placeholder="60" value={form.serves_price} onChange={set("serves_price")} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Photo</label>
                  <input ref={fileRef} data-testid="item-image" type="file" accept="image/*" onChange={onFile} className="hidden" />
                  <button type="button" onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-2 border border-dashed border-[#D4AF37]/40 rounded-lg py-3 text-[#A89F91] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors font-body text-sm">
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    {uploading ? "Uploading..." : form.image_url ? "Change photo" : "Upload photo"}
                  </button>
                  {form.image_url && (
                    <div className="mt-3 h-32 w-full overflow-hidden rounded-lg border border-[#D4AF37]/20">
                      <img src={resolveImg(form.image_url)} alt="preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
                <button data-testid="add-item-submit" type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 px-8 py-3.5 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase disabled:opacity-60">
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add to Menu
                </button>
              </form>
            </div>

            {/* Items list */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-3xl text-[#FDFBF7]">Menu Items</h2>
                <span className="font-body text-sm text-[#A89F91]">{items.length} items</span>
              </div>
              {Object.keys(grouped).length === 0 ? (
                <p className="font-body text-[#A89F91]">No items yet. Add your first dish.</p>
              ) : (
                <div className="space-y-10">
                  {Object.entries(grouped).map(([cat, list]) => (
                    <div key={cat}>
                      <h3 className="font-display italic text-xl text-[#D4AF37] mb-3">{cat}</h3>
                      <div className="space-y-3">
                        {list.map((it) => (
                          <div key={it.id} data-testid={`admin-item-${it.id}`} className="flex items-center gap-4 border border-[#D4AF37]/12 rounded-xl p-3 bg-[#140A0D]">
                            <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#1C0D12] shrink-0 flex items-center justify-center">
                              {it.image_url ? <img src={resolveImg(it.image_url)} alt={it.name} className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-[#A89F91]/40" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-display text-lg text-[#FDFBF7] truncate">{it.name}</h4>
                              <p className="font-body text-xs text-[#A89F91] truncate">{it.description}</p>
                              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 font-body text-xs text-[#D4AF37]">
                                {it.half_tray_price && <span>Half ${it.half_tray_price}</span>}
                                {it.full_tray_price && <span>Full ${it.full_tray_price}</span>}
                                {it.serves_price && <span>Serves {it.serves_qty || ""} ${it.serves_price}</span>}
                              </div>
                            </div>
                            <button data-testid={`edit-item-${it.id}`} onClick={() => setEditingItem(it)} className="shrink-0 p-2.5 text-[#A89F91] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors" aria-label="Edit">
                              <Pencil size={18} />
                            </button>
                            <button data-testid={`delete-item-${it.id}`} onClick={() => remove(it.id, it.name)} className="shrink-0 p-2.5 text-[#A89F91] hover:text-[#C92A6B] hover:bg-[#C92A6B]/10 rounded-lg transition-colors" aria-label="Delete">
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <h2 className="font-display text-3xl text-[#FDFBF7] mb-6">Booking Inquiries</h2>
            {bookings.length === 0 ? (
              <p className="font-body text-[#A89F91]">No inquiries yet.</p>
            ) : (
              <div className="space-y-4">
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`booking-${b.id}`} className="border border-[#D4AF37]/12 rounded-xl p-5 bg-[#140A0D]">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <h4 className="font-display text-xl text-[#FDFBF7]">{b.name} <span className="text-[#D4AF37] text-base">· {b.event_type}</span></h4>
                      <span className="font-body text-xs text-[#A89F91]">{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-1 font-body text-sm text-[#A89F91]">
                      <span>{b.email}</span><span>{b.phone}</span>
                      {b.event_date && <span>Date: {b.event_date}</span>}
                      {b.guest_count && <span>Guests: {b.guest_count}</span>}
                    </div>
                    {b.message && <p className="font-body text-sm text-[#FDFBF7]/80 mt-2">{b.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {editingItem && (
        <EditItemDialog
          item={editingItem}
          open={!!editingItem}
          onOpenChange={(v) => { if (!v) setEditingItem(null); }}
          onSaved={loadItems}
        />
      )}
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
